/**
 * Diff preview modal for the BOS "Sync Now" button.
 *
 * Adopted UX patterns from the legacy PlusGame backoffice:
 *  - Section per change type (new games, new packages, price changes, removed)
 *  - Cardinal copy in Thai stating that new items will default to disabled
 *  - Confirm button: "อัพเดทข้อมูล [N] รายการ"
 *
 * Brutalist styling: thick black borders, monospace technical labels, no
 * rounded corners on the inner cards.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';

import type { CatalogDiff } from '@/lib/wepayFunctions';
import { diffTotal } from '@/lib/wepayDiff';

interface Props {
  open: boolean;
  onClose: () => void;
  diff: CatalogDiff | null;
  onConfirm: () => void;
  isApplying?: boolean;
  errors?: string[];
}

const COLLAPSE_THRESHOLD = 5;

function Section({
  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string;
  count: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;
  return (
    <div className="border-2 border-black bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-black text-white"
      >
        <span className="font-mono text-xs tracking-wider uppercase">
          {title} ({count})
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && <div className="p-3 space-y-2">{children}</div>}
    </div>
  );
}

function ChangeCard({
  title,
  body,
  note,
  hint,
}: {
  title: string;
  body: string;
  note?: string;
  hint: string;
}) {
  return (
    <div className="border-2 border-black bg-white p-3">
      <div className="font-heading font-bold text-sm text-black">{title}</div>
      <div className="text-sm text-black mt-1">{body}</div>
      {note && (
        <div className="text-xs text-red-600 mt-1 font-mono">{note}</div>
      )}
      <div className="text-xs text-black/50 mt-2 font-mono">{hint}</div>
    </div>
  );
}

export default function SyncPreviewModal({
  open,
  onClose,
  diff,
  onConfirm,
  isApplying = false,
  errors = [],
}: Props) {
  const total = diff ? diffTotal(diff) : 0;
  const noChanges = !!diff && total === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !isApplying && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-3 border-black rounded-none bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold tracking-tight text-black">
            Pending wePAY updates ({total} รายการ)
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-black/60">
            ตรวจสอบข้อมูลก่อนอัพเดทระบบ — สินค้า/เกมใหม่จะถูกเพิ่มในสถานะ "ปิดใช้งาน"
          </DialogDescription>
        </DialogHeader>

        {!diff && (
          <div className="border-2 border-black p-6 text-center font-mono text-xs text-black/60">
            กำลังตรวจสอบ...
          </div>
        )}

        {diff && noChanges && (
          <div className="border-2 border-black p-6 text-center">
            <div className="font-heading font-bold text-base mb-1">
              ไม่มีการเปลี่ยนแปลง
            </div>
            <div className="font-mono text-xs text-black/60">
              ข้อมูลใน Firestore ตรงกับ wePAY แล้ว
            </div>
          </div>
        )}

        {diff && !noChanges && (
          <div className="space-y-3">
            <Section
              title="เกมใหม่"
              count={diff.newGames.length}
              defaultOpen={diff.newGames.length <= COLLAPSE_THRESHOLD}
            >
              {diff.newGames.map((g) => (
                <ChangeCard
                  key={`game-${g.gameKey}`}
                  title={`เกม ${g.serviceName}`}
                  body={`${g.amounts.length} แพ็คเกจ — pay_to_company: ${g.gameKey}`}
                  hint={`ระบบจะเพิ่มข้อมูลเกม ${g.serviceName} และสินค้าเกม ${g.serviceName} ทั้งหมด โดยมีสถานะเริ่มต้นเป็นปิดใช้งาน`}
                />
              ))}
            </Section>

            <Section
              title="สินค้าใหม่"
              count={diff.newPackages.length}
              defaultOpen={diff.newPackages.length <= COLLAPSE_THRESHOLD}
            >
              {diff.newPackages.map((p, i) => (
                <ChangeCard
                  key={`pkg-${p.gameKey}-${p.amount}-${i}`}
                  title={`สินค้าเกม ${p.gameName}`}
                  body={`${p.amount.toLocaleString('th-TH')} บาท — ${p.description}`}
                  hint="ระบบจะเพิ่มสินค้าเกม โดยมีสถานะเริ่มต้นเป็นปิดใช้งาน"
                />
              ))}
            </Section>

            <Section
              title="ราคาเปลี่ยนแปลง"
              count={diff.priceChanges.length}
              defaultOpen={diff.priceChanges.length <= COLLAPSE_THRESHOLD}
            >
              {diff.priceChanges.map((c, i) => (
                <ChangeCard
                  key={`price-${c.gameKey}-${c.amount}-${i}`}
                  title={`สินค้าเกม ${c.gameName}`}
                  body={`${c.amount.toLocaleString('th-TH')} บาท — ราคา Wepay เปลี่ยนจาก ${c.oldPrice.toLocaleString('th-TH')} → ${c.newPrice.toLocaleString('th-TH')} บาท`}
                  note="ระบบจะอัพเดทราคา Wepay เท่านั้น ราคาขายปัจจุบันจะไม่ถูกเปลี่ยน"
                  hint="ตรวจสอบราคาขายให้ตรงกับต้นทุนใหม่ก่อนเปิดใช้งาน"
                />
              ))}
            </Section>

            <Section
              title="สินค้าที่ wePAY ลบออก"
              count={diff.removedPackages.length}
              defaultOpen={diff.removedPackages.length <= COLLAPSE_THRESHOLD}
            >
              {diff.removedPackages.map((r, i) => (
                <ChangeCard
                  key={`rem-${r.gameKey}-${r.amount}-${i}`}
                  title={`สินค้าเกม ${r.gameName}`}
                  body={`${r.amount.toLocaleString('th-TH')} บาท`}
                  note="สินค้านี้ไม่อยู่ในรายการของ wePAY แล้ว — sync ปัจจุบันจะไม่ลบออกจาก Firestore"
                  hint="ตรวจสอบและปิดใช้งานด้วยตนเองหากยืนยันว่ายกเลิกแล้ว"
                />
              ))}
            </Section>

            {errors.length > 0 && (
              <div className="border-2 border-red-600 bg-red-50 p-3">
                <div className="font-heading font-bold text-sm text-red-600 mb-1">
                  Preview errors
                </div>
                <ul className="font-mono text-xs text-red-700 space-y-1">
                  {errors.map((e, i) => (
                    <li key={i}>• {e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isApplying}
            className="border-2 border-black rounded-none font-mono text-xs"
          >
            ยกเลิก
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isApplying || !diff || total === 0}
            className="bg-black text-white border-2 border-black rounded-none font-mono text-xs hover:bg-black/80"
          >
            {isApplying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                กำลังอัพเดท...
              </>
            ) : (
              `อัพเดทข้อมูล ${total} รายการ`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
